import { z } from 'zod';

// =============================================================================
//  ClientContact schemas — Story 2.2 (multi-pessoa PJ)
//  DR-033 Multi-pessoa PJ, FR-14.1–14.4
// =============================================================================

export const contactRoleSchema = z.enum([
  'dono',
  'socio',
  'gerente',
  'atendente',
  'financeiro',
  'fiscal',
  'comercial',
  'outro',
]);
export type ContactRoleSchema = z.infer<typeof contactRoleSchema>;

export const contactPreferredChannelSchema = z.enum(['whatsapp', 'email', 'ambos']);
export type ContactPreferredChannelSchema = z.infer<typeof contactPreferredChannelSchema>;

const emailField = z
  .union([z.string().email('Email inválido.'), z.literal('')])
  .optional()
  .nullable();

const phoneField = z.string().max(30).optional().nullable();

/**
 * Base schema — sem refinements.
 */
export const clientContactBaseSchema = z.object({
  name: z.string().min(1, 'Nome obrigatório.').max(200, 'Nome deve ter até 200 caracteres.'),
  role: contactRoleSchema,
  email: emailField,
  phone: phoneField,
  whatsapp: phoneField,
  notes: z.string().max(2000).optional().nullable(),
  is_active: z.boolean().default(true),
  preferred_channel: contactPreferredChannelSchema.default('whatsapp'),
  receives_billing_notifications: z.boolean().default(false),
  receives_fiscal_documents: z.boolean().default(false),
  inactivated_reason: z.string().max(500).optional().nullable(),
});

type ContactBaseShape = z.infer<typeof clientContactBaseSchema>;
type PartialContactBaseShape = Partial<ContactBaseShape>;

/**
 * Pelo menos um canal: email, phone OU whatsapp.
 * Aplicável em create (sempre) e em update (apenas quando campos canal são tocados).
 */
function hasAtLeastOneChannel(data: PartialContactBaseShape): boolean {
  return Boolean(
    (data.email && data.email !== '') ||
    (data.phone && data.phone !== '') ||
    (data.whatsapp && data.whatsapp !== ''),
  );
}

function refineContactPayload(
  data: PartialContactBaseShape,
  ctx: z.RefinementCtx,
  options: { requireChannel: boolean },
): void {
  if (options.requireChannel && !hasAtLeastOneChannel(data)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['email'],
      message: 'Informe ao menos um canal: email, telefone ou WhatsApp.',
    });
  }

  // Desativação requer motivo
  if (
    data.is_active === false &&
    (!data.inactivated_reason || data.inactivated_reason.trim() === '')
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['inactivated_reason'],
      message: 'Informe o motivo da desativação.',
    });
  }
}

export const createClientContactSchema = clientContactBaseSchema.superRefine((data, ctx) => {
  refineContactPayload(data, ctx, { requireChannel: true });
});
export type CreateClientContactDto = z.output<typeof createClientContactSchema>;
export type CreateClientContactInput = z.input<typeof createClientContactSchema>;

export const updateClientContactSchema = clientContactBaseSchema
  .partial()
  .superRefine((data, ctx) => {
    // Se TODOS os 3 canais foram explicitamente setados pra valor "vazio" (null/string vazia),
    // bloqueia. Se nenhum dos 3 foi tocado, OK (mantém o que tava).
    const touchedAny =
      data.email !== undefined || data.phone !== undefined || data.whatsapp !== undefined;
    if (touchedAny && !hasAtLeastOneChannel(data)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['email'],
        message: 'Informe ao menos um canal: email, telefone ou WhatsApp.',
      });
    }
    refineContactPayload(data, ctx, { requireChannel: false });
  });
export type UpdateClientContactDto = z.output<typeof updateClientContactSchema>;
export type UpdateClientContactInput = z.input<typeof updateClientContactSchema>;

/**
 * Query schema para GET /api/clients/:id/contacts
 */
export const queryClientContactsSchema = z.object({
  is_active: z.coerce.boolean().optional(),
  role: contactRoleSchema.optional(),
});
export type QueryClientContactsDto = z.infer<typeof queryClientContactsSchema>;
